/**
 * Department Layout
 *
 * Wraps department pages with department-specific sidebar.
 * Each department (Parts, Rental, Supplies) uses this layout
 * with different department prop for color scheme and links.
 */

import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from '@/shared/components/Sidebar/Sidebar';
import { Department } from '@/shared/components/Sidebar/sidebar-config';
import { useFirebaseStore } from '@/shared';
import { getLastDepartment, rememberDepartment } from '@/quizzes-react/utils/lastDepartment';

interface DepartmentLayoutProps {
  department: Department;
}

export function DepartmentLayout({ department }: DepartmentLayoutProps) {
  const { currentUser } = useFirebaseStore();

  /*
   * Drive the semantic accent tokens (--color-accent-*) for this department.
   * The attribute goes on <html>, not this layout's <div>, because modals and
   * drag overlays render through createPortal into document.body and would
   * otherwise fall outside the selector and paint in the default (Parts)
   * accent. See the [data-department] block in src/main-app/styles.css.
   */
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.department = department;
    rememberDepartment(department); // lets top-level pages (e.g. /quiz) link back into this department

    return () => {
      // Only clear if we're still the department that set it — on a
      // department-to-department navigation the incoming layout's effect may
      // already have claimed the attribute.
      if (root.dataset.department === department) {
        delete root.dataset.department;
      }
    };
  }, [department]);

  return (
    <div className="flex h-screen bg-surface-muted">
      {/* Department-specific sidebar */}
      <Sidebar department={department} userEmail={currentUser?.email} />

      {/* Main content area - child routes render here.
          The stable gutter (no width jump when a page starts to scroll) is painted
          with this element's background, so it carries the page plane: without
          it a short page showed a permanent 10px strip of the layout's
          surface-muted down the right edge — light against a dark page. */}
      <main className="flex-1 overflow-y-auto [scrollbar-gutter:stable] bg-surface-page">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * DepartmentLayout for top-level routes that belong to no department (e.g. /quiz):
 * shows the sidebar of the department the user last browsed. Read once on mount so
 * the sidebar doesn't swap departments mid-visit.
 */
export function LastDepartmentLayout() {
  const [department] = useState(getLastDepartment);
  return <DepartmentLayout department={department} />;
}
